<template>
    <div class="pb-22 bg-light-150">
        <div class="container">
            <PageIntro
                :title="$t('core.faq')"
                :subtitle="$t('core.faq_subtitle')"
                :breadcrumb-items="[[$t('core.faq'), 'faq']]"
            />
            <div class="grid grid-cols-12">
                <div class="col-span-12 lg:col-span-8">
                    <div class="bg-white rounded shadow-md-grey">
                        <SearchBlock
                            type="standard"
                            borderless
                            context="faq"
                            is-cancel-link
                            :link="'faq'"
                            :default-query="$route.query.search ? decodeURIComponent($route.query.search) : ''"
                        />
                    </div>
                    <div v-if="$route.query.search && questionCategories.length" class="mt-5">
                        {{ totalResults }}
                        {{ $tc('generic.results', questionCategories.length) }}:
                        <b>"{{ decodeURIComponent($route.query.search) }}"</b>
                    </div>
                    <div v-else-if="$route.query.search && !questionCategories.length" class="mt-5">
                        {{ $t('generic.no_results') }}:
                        <b>"{{ decodeURIComponent($route.query.search) }}"</b>
                    </div>
                    <div v-else-if="isIdQueryValid && questionCategories.length" class="mt-5">
                        {{ $tc('generic.results', questionCategories.length) }}:
                        <b>"{{ questionCategories[0].faq[0].question }}"</b>
                    </div>
                    <div v-if="questionCategories.length" class="gap-y-15 md:gap-y-18 flex flex-col mt-12">
                        <div v-for="(questionCategory, i) in questionCategories" :key="`faq-${questionCategory.id}`">
                            <h3 class="text-2xl font-HKGrotesk-SemiBold mb-5">{{ questionCategory.title }}</h3>
                            <Accordion :key="accordionKey" multiple :active-index="[i === 0 ? 0 : '']">
                                <AccordionTab
                                    v-for="answer in questionCategory.faq"
                                    :key="`problem-${answer.id}`"
                                    :header="answer.question"
                                >
                                    <div class="text-dark-200">
                                        <div class="tinymce-content" v-html="answer.answer"></div>
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </div>
                </div>

                <BookAppointmentCard />
            </div>
        </div>
    </div>
</template>

<script>
import PageIntro from '@/components/Generic/PageIntro.vue';
import SearchBlock from '@/components/Sections/SearchBlock/SearchBlock.vue';
import BookAppointmentCard from '@/components/Cards/BookAppointmentCard/BookAppointmentCard.vue';

export default {
    name: 'FAQ',
    components: { BookAppointmentCard, SearchBlock, PageIntro },
    async asyncData({ $axios, query }) {
        let questionCategories = [];
        let isIdQueryValid = false;
        const schemeEntities = [];

        try {
            await $axios
                .$get(query.search ? `/faq/search?q=${query.search}` : `/faq`)
                .then((res) => {
                    if (query.search) {
                        if (res.success === 1) {
                            questionCategories = res.results.reduce((acc, curr) => {
                                const category = acc.find((x) => x.id === curr.category.id);
                                if (!category) {
                                    acc.push({
                                        id: curr.category.id,
                                        name: curr.category.name,
                                        problems: [curr],
                                    });
                                } else {
                                    category.problems.push(curr);
                                }
                                return acc;
                            }, []);
                        }
                    } else if (res.success === 1) {
                        if (query.pid) {
                            const category = res.data.find((category) => {
                                return category.faq.find((question) => String(question.id) === query.pid);
                            });
                            if (category) {
                                isIdQueryValid = true;

                                category.faq = category.faq.filter((question) => String(question.id) === query.pid);
                                questionCategories.push(category);
                            } else {
                                questionCategories = res.data;
                            }
                        } else {
                            questionCategories = res.data;

                            for (const item of questionCategories[0].faq) {
                                schemeEntities.push({
                                    '@type': 'Question',
                                    name: item.question,
                                    acceptedAnswer: {
                                        '@type': 'Answer',
                                        text: item.answer.replace(/<[^>]*>/g, ''),
                                    },
                                });
                            }
                        }
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        } catch (err) {
            console.log(err);
        }

        // sadly the accordion doesn't always re-render properly after search terms have changed,
        // so we need a key on it to re-render every time, no matter what
        return {
            questionCategories,
            isIdQueryValid,
            accordionKey: Math.random(),
            schemeEntities,
        };
    },
    data() {
        return {
            query: '',
        };
    },
    head() {
        const script = [];

        script.push({
            type: 'application/ld+json',
            json: {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: this.schemeEntities,
            },
        });
        return {
            title: this.$t('core.faq'),
            meta: [
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: this.getOgTitle(this.$t('core.faq')),
                },
                {
                    hid: 'title',
                    name: 'title',
                    content: this.getOgTitle(this.$t('core.faq')),
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content: this.$t('core.faq_subtitle'),
                },
                {
                    hid: 'description',
                    name: 'description',
                    content: this.$t('core.faq_subtitle'),
                },
            ],
            script,
        };
    },
    computed: {
        totalResults() {
            let total = 0;
            for (const question of this.questionCategories) {
                console.log(question);
                total += question.faq.length;
            }
            return total;
        },
    },
    watchQuery: true,
    mounted() {
        if (this.$route.query.search) {
            this.query = decodeURIComponent(this.$route.query.search);
        }
    },
};
</script>
